import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useJoyride } from 'contexts/joyride';
import { useSnackbar } from 'contexts/snackbar';
import { omit } from 'lodash';
import { createContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import apiService from 'services/api';
import assignmentService from 'services/assignment';
import batchService from 'services/batch';
import submissionService from 'services/submission';
import { EXAMPLE_ASSIGNMENT_ID, SECTION_URL } from 'utils/constants';
import { cancelBatchEvaluation } from 'utils/helper';
import { v4 as uuid } from 'uuid';

const PageContext = createContext();

const PageProvider = (props) => {
    const {
        course,
        assignments,
        setAssignments,
        currentAssignment,
        batch,
        setBatch,
        submissions,
        setSubmissions,
        previewSubmissions,
        setPreviewSubmissions,
    } = useApp();
    const { auth } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const history = useHistory();
    const { setJoyridePaused } = useJoyride();
    const location = useLocation();
    const { t } = useTranslation('assignment');

    const [loading, setLoading] = useState(false);
    const [viewContentBankResourcesDialog, setViewContentBankResourcesDialog] = useState({ open: false });
    const [uploadAssignmentsDialog, setUploadAssignmentsDialog] = useState({ open: false });
    const [viewGradingSettingsDialog, setViewGradingSettingsDialog] = useState({ open: false });
    const [pushRubricDialog, setPushRubricDialog] = useState({ open: false });
    const [apiSubmissions, setAPISubmissions] = useState(null);
    const [uploadSubmissionsDialog, setUploadSubmissionsDialog] = useState({ open: false });
    const [uploadPreviewSubmissionsDialog, setUploadPreviewSubmissionsDialog] = useState({ open: false });
    const [canCancelBatch, setCanCancelBatch] = useState(true);

    const isPreviewGenerating = batch?.some(({ type }) => type === 'fine-tune');
    const isSubmissionsGenerating = batch?.some(({ type }) => type !== 'fine-tune');

    const attachPreviewFiles = (files) => {
        const timestamp = new Date().getTime();
        setAssignments((current) => {
            return current.map((a) => {
                if (a.id !== currentAssignment.id) return a;
                try {
                    const previewFiles = [...(a?.preview_files || []), ...files].map((file) => ({
                        ...file,
                        is_preview_up_to_date: false,
                    }));
                    const updatedAssignment = {
                        ...a,
                        modified_on: new Date().getTime(),
                        has_seen_preview: false,
                        preview_files: previewFiles,
                    };
                    const batch = {
                        id: uuid(),
                        active: true,
                        created_on: timestamp,
                        modified_on: timestamp,
                        owner_account_id: auth.id,
                        course_id: course.id,
                        assignment_id: updatedAssignment.id,
                        submissions: updatedAssignment.preview_files.map((file) => ({
                            submitted_at: timestamp,
                            files: [
                                {
                                    ...file,
                                    owner_account_id: auth.id,
                                    course_id: course.id,
                                    assignment_id: updatedAssignment.id,
                                },
                            ],
                        })),
                        is_finished: false,
                        type: 'fine-tune',
                    };
                    setBatch((current) => [...(current || []), batch]);
                    setPreviewSubmissions((current) => {
                        current.forEach((ps) => {
                            submissionService.deleteSubmission(ps.id);
                        });
                        return [];
                    });
                    batchService.createSubmissions(batch);
                    assignmentService.updateAssignment(currentAssignment.id, omit(updatedAssignment, ['_id']));
                    return updatedAssignment;
                } catch (e) {
                    console.error(e);
                }
                return a;
            });
        });
    };

    const detachPreviewFile = (file) => {
        setAssignments((current) => {
            return current.map((a) => {
                if (a.id !== currentAssignment.id) return a;
                const previewFiles = a.preview_files.filter(({ id }) => id !== file.id);
                assignmentService.updateAssignment(currentAssignment.id, {
                    modified_on: new Date().getTime(),
                    preview_files: previewFiles,
                });
                const previewSubmissionToDelete = previewSubmissions.find(
                    (ps) => ps.files[0].key === file.data.key || ps.files[0].key.includes(file.data.key)
                );
                if (previewSubmissionToDelete) {
                    setPreviewSubmissions((current) => {
                        return current.filter((ps) => ps.id !== previewSubmissionToDelete.id);
                    });
                    submissionService.deleteSubmission(previewSubmissionToDelete.id);
                }
                return {
                    ...a,
                    preview_files: a.preview_files.filter(({ id }) => id !== file.id),
                };
            });
        });
    };

    const createPreviewSubmissions = async () => {
        setCanCancelBatch(false);
        const timestamp = new Date().getTime();
        const id = uuid();
        const batch = {
            id,
            active: true,
            created_on: timestamp,
            modified_on: timestamp,
            owner_account_id: auth.id,
            course_id: course.id,
            assignment_id: currentAssignment.id,
            submissions: currentAssignment.preview_files.map((file) => ({
                submitted_at: timestamp,
                files: [
                    {
                        ...file,
                        owner_account_id: auth.id,
                        course_id: course.id,
                        assignment_id: currentAssignment.id,
                    },
                ],
            })),
            is_finished: false,
            type: 'fine-tune',
        };
        setAssignments((current) => {
            return current.map((a) => {
                if (a.id !== currentAssignment.id) return a;
                const updatedAssignment = {
                    ...a,
                    preview_files: a.preview_files.map((pf) => ({
                        ...pf,
                        modified_on: new Date().getTime(),
                        is_preview_up_to_date: false,
                    })),
                };
                assignmentService.updateAssignment(currentAssignment.id, omit(updatedAssignment, ['_id']));
                return updatedAssignment;
            });
        });
        setBatch((current) => [...(current || []), batch]);
        setPreviewSubmissions((current) => {
            current.forEach((ps) => {
                submissionService.deleteSubmission(ps.id);
            });
            return [];
        });
        try {
            await batchService.createSubmissions(batch);
        } catch (e) {
            console.error(e);
        } finally {
            setCanCancelBatch(true);
        }
    };

    const cancelBatch = () => {
        setDialog({
            open: true,
            message: t('dialog.cancel-submission.title', 'Cancel Submission Processing?'),
            description: t(
                'dialog.cancel-submission.description',
                "You'll need to regenerate a preview for any submissions that haven't finished processing."
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                closeDialog();
                setCanCancelBatch(false);
                await cancelBatchEvaluation({ currentAssignment, batch });
                setBatch((current) => current.filter(({ id }) => id === batch.id));
                setCanCancelBatch(true);
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const startFineTuning = () => {
        history.push(`/assignments/${currentAssignment.id}/preview`);
    };

    const proceedToGradingFeedback = () => {
        setAssignments((current) => {
            return current.map((a) => {
                if (a.id !== currentAssignment.id) return a;
                const updatedAssignment = {
                    ...a,
                    modified_on: new Date().getTime(),
                    has_finished_preview: true,
                };
                assignmentService.updateAssignment(updatedAssignment.id, omit(updatedAssignment, ['_id']));
                return updatedAssignment;
            });
        });
    };

    const skipFineTuning = () => {
        setDialog({
            open: true,
            message: t('dialog.confirm-skip-fine-tuning.title', 'Skip Fine-Tuning?'),
            description: t(
                'dialog.confirm-skip-fine-tuning.description',
                'Fine-tuning ensures grades and feedback align with your expectations, and skipping this step may reduce accuracy. Fine-tuning will no longer be available once submissions are uploaded.'
            ),
            note: '',
            variant: 'primary',
            actionButtonText: t('common:controls.skip-and-proceed', 'Skip & Proceed'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                closeDialog();
                if (Boolean(batch.length)) {
                    setBatch((current) => current.filter(({ id }) => id === batch.id));
                    cancelBatchEvaluation({ currentAssignment, batch });
                }
                setAssignments((current) => {
                    return current.map((a) => {
                        if (a.id !== currentAssignment.id) return a;
                        const updatedAssignment = {
                            ...a,
                            modified_on: new Date().getTime(),
                            has_finished_preview: true,
                        };
                        assignmentService.updateAssignment(updatedAssignment.id, omit(updatedAssignment, ['_id']));
                        return updatedAssignment;
                    });
                });
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const restartPreviewFineTune = () => {
        const timestamp = new Date().getTime();
        setAssignments((current) => {
            return current.map((a) => {
                if (a.id !== currentAssignment.id) return a;
                const updatedAssignment = {
                    ...a,
                    modified_on: timestamp,
                    has_seen_preview: false,
                    has_finished_preview: false,
                    preview_files: (a.preview_files || []).map((pf) => {
                        const previewSubmissionToDelete = previewSubmissions.find(
                            (ps) => ps.files[0].key === pf.data.key || ps.files[0].key.includes(pf.data.key)
                        );
                        if (previewSubmissionToDelete) {
                            setPreviewSubmissions((current) => {
                                return current.filter((ps) => ps.id !== previewSubmissionToDelete.id);
                            });
                            submissionService.deleteSubmission(previewSubmissionToDelete.id);
                        }
                        return {
                            ...pf,
                            modified_on: timestamp,
                            is_preview_up_to_date: false,
                        };
                    }),
                };
                assignmentService.updateAssignment(updatedAssignment.id, omit(updatedAssignment, ['_id']));
                return updatedAssignment;
            });
        });
    };

    const resetSubmissions = () => {
        setDialog({
            open: true,
            message: t('dialog.erase-assignment-data.title', 'Erase Existing Assignment Data?'),
            description: t(
                'dialog.erase-assignment-data.description',
                'If you reset, any existing submissions and associated grades/feedback will be permanently deleted.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.continue', 'Continue'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: async () => {
                setSubmissions([]);
                setBatch([]);
                closeDialog();
                await Promise.all([
                    await assignmentService.deleteAssignmentSubmissions(currentAssignment.id),
                    await assignmentService.deleteAssignmentBatch(currentAssignment.id),
                ]);
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const createSubmissions = async (files) => {
        setCanCancelBatch(false);
        const timestamp = new Date().getTime();
        const id = uuid();
        const batch = {
            id,
            active: true,
            created_on: timestamp,
            modified_on: timestamp,
            owner_account_id: auth.id,
            course_id: course.id,
            assignment_id: currentAssignment.id,
            submissions: files,
            is_finished: false,
        };
        setBatch((current) => [...(current || []), batch]);
        try {
            await batchService.createSubmissions(batch);
        } catch (e) {
            console.error(e);
        } finally {
            setCanCancelBatch(true);
        }
    };

    async function fetchAllLmsSubmissions({ courseId, assignmentId, accessToken }) {
        let submissions = [];
        let url = null;
        let sections = [];

        const section_pre = await apiService.getEnvData({
            course_id: courseId,
            accessToken,
            assignment_id: assignmentId,
            url,
        });

        const section_req_body = {
            course_id: courseId,
            accessToken,
            assignment_id: assignmentId,
            API_URL: section_pre.API_URL,
            PLATFORM: section_pre.PLATFORM,
        };

        sections = await fetch(SECTION_URL, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(section_req_body),
        }).then(async (res) => await res.json());
        do {
            const currentAccessToken = localStorage.getItem('access_token');
            const data = await apiService.getSubmissions({
                course_id: courseId,
                accessToken: currentAccessToken,
                assignment_id: assignmentId,
                url,
            });

            submissions.push(...data.submissions);
            // Parse Link header for next page URL
            url = null;

            if (data.has_next) {
                url = data.next_url;
                // (Or use manual parsing as shown above if not using the library)
            }
        } while (url);
        // Remove unsubmitted entries
        if (process.env.REACT_APP_KIND === 'D2L') {
            submissions = submissions.filter((s) => s.Submissions?.length >= 0);
        } else {
            submissions = submissions.filter((s) => s.workflow_state !== 'unsubmitted' && s.attachments?.length > 0);
        }

        if (sections) {
            const results = submissions.map((submission) => {
                let studentSections = [];
                if (sections) {
                    if (process.env.REACT_APP_KIND === 'D2L' && sections.Enrollments?.length > 0) {
                        sections.Enrollments?.forEach((section) => {
                            const { students, ...rest } = section;
                            let student = null;
                            if (process.env.REACT_APP_KIND === 'D2L') {
                                student = section.Enrollments?.find(
                                    (id) => `${id}` === `${submission?.Entity?.EntityId}`
                                );
                            } else {
                                student = section.students?.find(
                                    (student) => `${student.id}` === `${submission.user_id}`
                                );
                            }

                            if (student) {
                                studentSections.push(rest);
                            }
                        });
                    } else if (process.env.REACT_APP_KIND === 'CANVAS' && sections.students?.length > 0) {
                        sections.students?.forEach((section) => {
                            const { students, ...rest } = section;
                            let student = null;
                            student = section.students?.find((student) => `${student.id}` === `${submission.user_id}`);

                            if (student) {
                                studentSections.push(rest);
                            }
                        });
                    }
                }
                submission.sections = studentSections;
                return submission;
            });
            return results;
        }
        return submissions;
    }

    const importAPISubmissions = async () => {
        setLoading(true);
        try {
            const accessToken = localStorage.getItem('access_token');
            const course_id = auth.course_id;

            // track submissions that need to be deleted from our db because a new attempt has been submitted
            const submissionsToDelete = [];

            let apiSubmissions;

            apiSubmissions = await fetchAllLmsSubmissions({
                courseId: course_id,
                assignmentId: `${currentAssignment.api_data.id}`,
                accessToken,
            });

            // check which submissions are new based on submitter user id and attempt
            apiSubmissions = apiSubmissions.filter((incomingSubmission) => {
                const existingSubmission = submissions.find((submission) => {
                    if (process.env.REACT_APP_KIND === 'D2L') {
                        return String(submission.api.user.id) === String(incomingSubmission?.Entity?.EntityId);
                    }
                    return String(submission.api.user.id) === String(incomingSubmission.user.id);
                });
                if (!existingSubmission) return true;
                if (process.env.REACT_APP_KIND === 'D2L') {
                    const latestSubmission = incomingSubmission.Submissions?.[0];
                    const incomingFileId = latestSubmission?.Files?.[0]?.FileId;
                    const existingFileId = existingSubmission.files?.[0]?.api?.file?.id;
                    const isNewAttempt = incomingFileId && String(incomingFileId) !== String(existingFileId);
                    if (isNewAttempt) {
                        submissionsToDelete.push(existingSubmission);
                        return true;
                    }
                } else {
                    const isNewAttempt = incomingSubmission.attempt > existingSubmission.files[0].api.file.attempt;
                    if (isNewAttempt) {
                        submissionsToDelete.push(existingSubmission);
                        return true;
                    }
                }

                return false;
            });

            setLoading(false);
            if (apiSubmissions.length) {
                setDialog({
                    open: true,
                    message: t('dialog.import-submissions.title', 'Import Submissions?'),
                    description: t(
                        'dialog.import-submissions.description',
                        'We found ({{count}}) assignments to import for this assignment. Do you want to import them now?',
                        { count: apiSubmissions.length }
                    ),
                    note: t(
                        'dialog.import-submissions.note',
                        'Note: Once you import, TAs can view and grade submissions for their assigned sections.'
                    ),
                    variant: 'primary',
                    actionButtonText: t('common:controls.continue', 'Continue'),
                    cancelButtonText: t('common:controls.cancel', 'Cancel'),
                    onAction: async () => {
                        setAPISubmissions({
                            toCreate: apiSubmissions,
                            toDelete: submissionsToDelete,
                        });
                        setUploadSubmissionsDialog({ open: true });
                        closeDialog();
                    },
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
            } else {
                setDialog({
                    open: true,
                    message: t('dialog.no-submission-found.title', 'No Submission Found'),
                    description: t(
                        'dialog.no-submission-found.description',
                        "It looks like there aren't any student submissions for this assignment yet. A minimum of one submission is required to import submissions."
                    ),
                    note: '',
                    variant: 'destructive',
                    actionButtonText: t('common:controls.ok', 'Ok'),
                    cancelButtonText: '',
                    onAction: () => {
                        closeDialog();
                    },
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
            }
        } catch (e) {
            console.error(e);
            setLoading(false);
            return message.error(
                t('common:message.something-went-wrong-try-again', 'Something went wrong. Please try again later.')
            );
        }
    };

    useEffect(() => {
        if (!assignments) return;
        if (
            location.pathname.includes(EXAMPLE_ASSIGNMENT_ID) &&
            !assignments.some(({ id }) => id === EXAMPLE_ASSIGNMENT_ID)
        ) {
            history.replace('/assignments');
        }
    }, [location, assignments, history]);

    useEffect(() => {
        setJoyridePaused(false);
        return () => setJoyridePaused(true);
    }, [setJoyridePaused]);

    return (
        <PageContext.Provider
            value={{
                loading,
                batch,
                submissions,
                apiSubmissions,
                setAPISubmissions,
                viewContentBankResourcesDialog,
                setViewContentBankResourcesDialog,
                viewGradingSettingsDialog,
                setViewGradingSettingsDialog,
                uploadSubmissionsDialog,
                setUploadSubmissionsDialog,
                uploadPreviewSubmissionsDialog,
                pushRubricDialog,
                setPushRubricDialog,
                setUploadPreviewSubmissionsDialog,
                createSubmissions,
                restartPreviewFineTune,
                importAPISubmissions,
                uploadAssignmentsDialog,
                setUploadAssignmentsDialog,
                canCancelBatch,
                isPreviewGenerating,
                isSubmissionsGenerating,
                attachPreviewFiles,
                detachPreviewFile,
                createPreviewSubmissions,
                cancelBatch,
                resetSubmissions,
                startFineTuning,
                proceedToGradingFeedback,
                skipFineTuning,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
